// Signal-Room import contract (PLAN.md rev. 4, #5/#7/#11) — the versioned Zod schemas the
// import endpoint validates against, plus the shared pillar-name normalizer. The wire-level
// mirror of this contract is checked in at docs/plans/signal-room-import.schema.json (the
// exporter in ~/Developer/signal-room validates against THAT file); keep the two in sync and
// bump SR_SCHEMA_VERSION on any breaking change. The server accepts versions
// [SR_MIN_SUPPORTED_VERSION, SR_SCHEMA_VERSION] (N/N-1 window, server deploys first).
import { z } from "zod";
import { MEDIA_FORMATS } from "./pillarFormats.js";

export const SR_SCHEMA_VERSION = 2;
export const SR_MIN_SUPPORTED_VERSION = 1;

/** ISO week, e.g. 2026-W42 (v2 `weekIso`). */
export const SR_WEEK_ISO_RE = /^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/;

// Payload caps (#4/#11). The request body itself is capped at 1 MB by the route parser.
export const SR_MAX_POSTS_PER_REQUEST = 25;
export const SR_MAX_PILLARS = 20;
export const SR_MAX_FACTS_PER_PILLAR = 8;
export const SR_MAX_HIGHLIGHT_COMPANIES = 20;
/** Carousel slides (PLAN 2026-10-11 item 1): Instagram allows at most 10 items per carousel. */
export const SR_MAX_SLIDES_PER_POST = 10;
/** All slides of one request together (item 8): keeps the image staging of one import far below the claim
 *  timeout. A hotel week is 7 posts, so 80 leaves room for 8 full carousels. */
export const SR_MAX_SLIDES_PER_REQUEST = 80;

/**
 * Shared pillar-name normalizer (#8): deterministic, diacritic-free, whitespace-collapsed.
 * Persisted as instagram_pillars.normalized_name AND used as the instagram_pillar_prefs join
 * key, so prefs survive cosmetic renames ("Fastnacht " vs "fastnacht"). Must stay stable —
 * changing it orphans existing prefs.
 */
export function normalizePillarName(name: string): string {
  return name
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "") // strip combining diacritics (ä→a, é→e)
    .toLowerCase()
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, " ") // collapse punctuation/whitespace runs
    .trim();
}

/** Namespaced idempotency key (#5): `sr:<client-id>:<brief-key>` → pillar_posts.source_ref. */
export function buildSourceRef(clientId: string, key: string): string {
  return `sr:${clientId}:${key}`;
}

export const SR_SOURCE_REF_PREFIX = "sr:";

/** THE check for "is this row Signal-Room-sourced" — every gate (publish kill switch, edit
 *  guard, UI badges) must go through this so a prefix change can't silently miss a site. */
export function isSignalRoomSourced(sourceRef: string | null | undefined): boolean {
  return sourceRef != null && sourceRef.startsWith(SR_SOURCE_REF_PREFIX);
}

// ── Payload schemas (v1) ────────────────────────────────────────────────────────────────────

const srKey = z.string().min(1).max(120).regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/,
  "key must be alphanumeric with . _ - separators (e.g. brief-42)");

/** Video and poster URLs (B2, PLAN §11 B5): https only — the fetcher refuses every other scheme anyway, the
 *  contract says so up front. */
const srHttpsUrl = z.string().url().max(2048).refine((u) => /^https:\/\//i.test(u), "must be an https URL");

export const srPostSchema = z.object({
  key: srKey, // durable exporter-side id, e.g. "brief-42"; server namespaces it to sr:<client>:<key>
  title: z.string().min(1).max(300),
  content: z.string().min(1).max(10_000),
  hashtags: z.array(z.string().min(1).max(80)).max(30).default([]),
  imageUrl: z.string().url().nullish(), // fetched via the strict fetcher, never stored raw
  pillarName: z.string().max(200).nullish(),
  platform: z.enum(["instagram", "linkedin", "facebook"]).default("instagram"),
  callToAction: z.string().max(500).nullish(),
  /** CAS guard (#5): the pillar_posts.version the exporter last saw. An update whose baseVersion
   *  no longer matches (customer edited / concurrent import) returns `conflict`, never overwrites. */
  baseVersion: z.number().int().min(1).optional(),
  /** v2 (Hotel-Edition B1, docs/contracts/signal-room-import.v2.json): what kind of post this is. Omitted
   *  = v1 behaviour (derived from platform for display). A 'reel' needs videoUrl (B2, "kein Reel ohne Video"). */
  mediaFormat: z.enum(MEDIA_FORMATS).optional(),
  /** v2 (Hotel-Edition B2): the reel's MP4 (H.264/AAC, +faststart, max. 100 MB), https only. Fetched by
   *  srVideoFetch.ts, never stored raw. Only read for mediaFormat 'reel'; ignored on every other post. */
  videoUrl: srHttpsUrl.optional(),
  /** v2 (Hotel-Edition B2): the reel's preview image (jpeg/png/webp), https only, strict image fetcher.
   *  Only read for mediaFormat 'reel'. A failed poster fetch keeps the reel (shown without preview). */
  posterUrl: srHttpsUrl.nullish(),
  /** Carousel (PLAN 2026-10-11): the slides in order, 1..10 https URLs. imageUrl, when also sent, must be
   *  slide 1; an old exporter that sends only imageUrl is a one-slide post. Never on a reel. */
  images: z.array(srHttpsUrl).min(1).max(SR_MAX_SLIDES_PER_POST).optional(),
}).strict()
  .refine((p) => !p.images || p.imageUrl == null || p.imageUrl === p.images[0], {
    message: "imageUrl must equal images[0]",
    path: ["imageUrl"],
  })
  .refine((p) => !(p.images && p.mediaFormat === "reel"), {
    message: "a reel cannot carry images",
    path: ["images"],
  });
export type SrPost = z.infer<typeof srPostSchema>;

/** The slides of a post in order: images, else [imageUrl], else none. Every server path reads slides only
 *  through this, so a v1 payload and a carousel go through the same code. */
export function slideUrls(p: Pick<SrPost, "images" | "imageUrl">): string[] {
  if (p.images?.length) return p.images;
  return p.imageUrl ? [p.imageUrl] : [];
}

export const srPillarSchema = z.object({
  rank: z.number().int().min(1).max(99),
  name: z.string().min(1).max(200),
  claim: z.string().max(1000).nullish(),
  status: z.enum(["never-posted", "dormant", "underused", "workhorse"]).nullish(),
  facts: z.array(z.object({
    text: z.string().min(1).max(1000),
    source: z.string().max(500).nullish(),
  }).strict()).max(SR_MAX_FACTS_PER_PILLAR).default([]),
  series: z.string().max(300).nullish(),
  cadence: z.string().max(200).nullish(), // "Takt"
  seedIdeas: z.array(z.string().min(1).max(500)).max(12).default([]),
  keywords: z.array(z.string().min(1).max(100)).max(20).default([]),
  coverage: z.record(z.unknown()).default({}),
}).strict();
export type SrPillar = z.infer<typeof srPillarSchema>;

export const srMarketSchema = z.object({
  /** ISO-Monday (Europe/Berlin) of the week the card describes, YYYY-MM-DD. Validated server-side
   *  to actually BE a Monday. */
  weekStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  sourceVersion: z.number().int().min(1),
  /** GLOBAL-ONLY card content — competitor material must never appear here (tenancy #10). */
  card: z.record(z.unknown()),
  /** Per-company highlights, mapped by Signal-Room client id. Card + ALL highlights import in
   *  one transaction; the snapshot is only readable once complete. */
  highlights: z.array(z.object({
    clientId: z.string().min(1).max(120),
    items: z.array(z.record(z.unknown())).max(8),
  }).strict()).max(SR_MAX_HIGHLIGHT_COMPANIES).default([]),
}).strict();
export type SrMarket = z.infer<typeof srMarketSchema>;

export const srImportPayloadSchema = z.object({
  version: z.number().int(),
  /** Exporter-minted UUID for the whole request — the ledger replay key (#7a). */
  importId: z.string().uuid(),
  /** Validate-only mode (#7c): full validation + ledger row, zero posts/pillars/snapshot writes. */
  dryRun: z.boolean().default(false),
  /** The Signal-Room creator id of the target company. Required for posts/pillars; market-only
   *  payloads may omit it (highlights carry their own clientIds). */
  clientId: z.string().min(1).max(120).optional(),
  posts: z.array(srPostSchema).max(SR_MAX_POSTS_PER_REQUEST).optional(),
  pillars: z.array(srPillarSchema).max(SR_MAX_PILLARS).optional(),
  market: srMarketSchema.optional(),
  /** v2: the ISO week (YYYY-Www, Europe/Berlin) the posts are written for. Hotel weeks are matched against
   *  it (PLAN §6.1, part C); B1 only validates it. */
  weekIso: z.string().regex(SR_WEEK_ISO_RE, "weekIso must look like 2026-W42").optional(),
}).strict()
  .refine((p) => !(p.posts?.length || p.pillars?.length) || !!p.clientId, {
    message: "clientId is required when posts or pillars are present",
    path: ["clientId"],
  })
  // Media staging and its cleanup are keyed by post key (srMediaStaging.ts): a second post with the same key would
  // overwrite the first one's staging and could delete committed media (review carousel R1 H1).
  .refine((p) => new Set((p.posts ?? []).map((post) => post.key)).size === (p.posts ?? []).length, {
    message: "post keys must be unique within one request",
    path: ["posts"],
  })
  .refine((p) => (p.posts ?? []).reduce((n, post) => n + (post.images?.length ?? 0), 0) <= SR_MAX_SLIDES_PER_REQUEST, {
    message: `at most ${SR_MAX_SLIDES_PER_REQUEST} carousel slides per request`,
    path: ["posts"],
  })
  .refine((p) => !!(p.posts?.length || p.pillars?.length || p.market), {
    message: "payload must carry posts, pillars, or market",
    path: ["posts"],
  });
export type SrImportPayload = z.infer<typeof srImportPayloadSchema>;

// ── Response contract (#7) ──────────────────────────────────────────────────────────────────

export type SrItemStatus =
  | "created"
  | "updated"
  | "skipped_locked"       // post is approved/scheduled/posted/DFY-frozen — permanent no-op
  | "skipped_user_edited"  // customer edited the row — permanent no-op (fetch current state)
  | "conflict"             // baseVersion mismatch — NOT retryable as-is; re-export from current
  | "rejected";            // validation/mapping/state failure; `retryable` says whether to retry

export type SrItemDecision = {
  key: string;
  status: SrItemStatus;
  reason?: string;
  retryable?: boolean;
  version?: number;       // current row version after the decision
  imageStatus?: "stored" | "failed" | "none";
};

export type SrImportResponse = {
  importId: string;
  dryRun: boolean;
  replayed?: boolean;      // true → this is the recorded result of an earlier identical import
  posts?: SrItemDecision[];
  pillars?: { upserted: number; retired: number };
  market?: { weekStart: string; applied: boolean; reason?: string };
  counts: Record<string, number>;
};
